import { URLSearchParams } from 'url';

import { UserDetailsPartial } from 'src/auth/types';
import { StepEnum } from 'src/features/step-list/constants';
import { PathNames } from 'src/shared/enum/PathNames';
import { Basket } from 'src/store/slices/basket-slice';
import { BookingState } from 'src/store/types/booking-state';
import { FALLBACK_LNG, LanguagesCode } from 'src/i18n/constants';
import { RoommateData } from 'src/features/multi-occupancy/types';

import { PaymentPlanSelection } from './types/PaymentPlanSelection';
import { AppStorage } from './types/AppStorage';
import { DualOccupant } from './types/DualOccupant';

const STORAGE_KEY = 'app_student_booking';
const APP_VERSION = '1.0.0';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isAppStorage(obj: any): obj is AppStorage {
    if (!obj || typeof obj.version !== 'string' || !obj.auth0) {
        return false;
    }

    const { auth0 } = obj;
    const hasValidIdToken =
        'id_token' in auth0 && (typeof auth0.id_token === 'string' || auth0.id_token === null);
    const hasValidAccessToken =
        'access_token' in auth0 &&
        (typeof auth0.access_token === 'string' || auth0.access_token === null);

    return hasValidIdToken && hasValidAccessToken;
}

const getStorage = (): AppStorage => {
    const storage = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!isAppStorage(storage) || storage.version !== APP_VERSION) {
        const preservedBasket = isAppStorage(storage) && storage.basket ? storage.basket : null;

        return {
            version: APP_VERSION,
            auth0: {
                id_token: null,
                access_token: null,
                tokenExpired: false,
            },
            basket: preservedBasket,
            navigationState: {
                page: PathNames.SUMMARY,
                step: StepEnum.YourDetails,
            },
            redirectState: {
                room: null,
                academicYear: null,
                pid: null,
                cbl: null,
                rebooker: null,
                paymentPlanId: null,
            },
            user: null,
            booking: null,
            paymentPlanSelection: null,
            dualOccupant: null,
            language: FALLBACK_LNG,
            amend: {
                vitaRef: null,
                roommates: [],
            },
        };
    }

    return storage;
};

const setStorage = (data: Partial<AppStorage>) => {
    const storage = getStorage();
    const newStorage = { ...storage, ...data };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage));
};

const removeStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
};

const setNavigationState = (path: PathNames, step: StepEnum) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        navigationState: {
            page: path,
            step,
        },
    });
};

const getIdToken = (): string | null => {
    const storage = getStorage();
    return storage.auth0.id_token;
};

const resetAuth0Storage = () => {
    const storage = getStorage();
    setStorage({
        ...storage,
        auth0: {
            id_token: null,
            access_token: null,
            tokenExpired: false,
        },
    });
};

const setTokenStorageExpired = (expired: boolean) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        auth0: {
            ...storage.auth0,
            tokenExpired: expired,
        },
    });
};

const getTokenStorageExpired = (): boolean => {
    const storage = getStorage();
    return storage.auth0.tokenExpired;
};

const setRedirectState = (
    roomCode: string,
    academicYear: string,
    pid: string,
    cbl: string,
    rebooker: string,
    paymentPlanId: string
) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        redirectState: {
            room: roomCode,
            academicYear,
            pid,
            cbl,
            rebooker,
            paymentPlanId,
        },
    });
};

const updateRedirectState = (
    roomCode: string,
    academicYear: string,
    pid: string | null,
    cbl: string | null,
    rebooker: string | null,
    paymentPlanId: string | null
) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        redirectState: {
            room: roomCode,
            academicYear,
            pid,
            cbl,
            rebooker,
            paymentPlanId,
        },
    });
};

const clearStorageOnRoomOrYearChange = (roomCode: string, academicYear: string) => {
    const storage = getStorage();
    if (
        storage.redirectState.room &&
        storage.redirectState.academicYear &&
        (storage.redirectState.room !== roomCode ||
            storage.redirectState.academicYear !== academicYear)
    ) {
        removeStorage();
    }
};

const getBasketStorage = (): Basket | null => {
    const storage = getStorage();
    return storage.basket;
};

const getUserStorage = (): UserDetailsPartial | null => {
    const storage = getStorage();
    return storage.user;
};

const setBasketStorage = (data: Basket) => {
    const storage = getStorage();
    const newBasket = { ...storage.basket, ...data };
    setStorage({ basket: newBasket });
};

const setUserStorage = (data: UserDetailsPartial) => {
    const storage = getStorage();
    const newUserDetails = { ...storage.user, ...data };
    setStorage({ user: newUserDetails });
};

const getAcademicYear = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { academicYear = null } = storage.redirectState;
    const academicYearSearchParam = searchParams.get('academicYear');
    const isSearchParams = !searchParams.size;

    const hasAcademicYearSearchParam =
        typeof academicYearSearchParam === 'string' && academicYearSearchParam.trim().length > 0;

    if (hasAcademicYearSearchParam) {
        return academicYearSearchParam;
    }

    if (academicYear && academicYear.trim().length > 0 && isSearchParams) {
        return academicYear;
    }

    return null;
};

const getRoomCode = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { room = null } = storage.redirectState;
    const roomCodeSearchParam = searchParams.get('roomCode');
    const isSearchParams = !searchParams.size;

    const hasRoomCodeSearchParam =
        typeof roomCodeSearchParam === 'string' && roomCodeSearchParam.trim().length > 0;

    if (hasRoomCodeSearchParam) {
        return roomCodeSearchParam;
    }

    if (room && room.trim().length > 0 && isSearchParams) {
        return room;
    }

    return null;
};

const getPromotionId = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { pid = null } = storage.redirectState;
    const pidSearchParam = searchParams.get('pid');
    const isSearchParams = !searchParams.size;

    const hasPidSearchParam =
        typeof pidSearchParam === 'string' && pidSearchParam.trim().length > 0;

    if (hasPidSearchParam) {
        return pidSearchParam;
    }

    if (pid && pid.trim().length > 0 && isSearchParams) {
        return pid;
    }

    return null;
};

const getCbl = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { cbl = null } = storage.redirectState;
    const cblSearchParam = searchParams.get('cbl');
    const isSearchParams = !searchParams.size;

    const hasCblSearchParam =
        typeof cblSearchParam === 'string' && cblSearchParam.trim().length > 0;

    if (hasCblSearchParam) {
        return cblSearchParam;
    }

    if (cbl && cbl.trim().length > 0 && isSearchParams) {
        return cbl;
    }

    return null;
};

const getRebooker = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { rebooker = null } = storage.redirectState;
    const rebookerSearchParam = searchParams.get('rebooker');
    const isSearchParams = !searchParams.size;

    const hasRebookerSearchParam =
        typeof rebookerSearchParam === 'string' && rebookerSearchParam.trim().length > 0;

    if (hasRebookerSearchParam) {
        return rebookerSearchParam;
    }

    if (rebooker && rebooker.trim().length > 0 && isSearchParams) {
        return rebooker;
    }

    return null;
};

const getPaymentPlanId = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { paymentPlanId = null } = storage.redirectState;
    const paymentPlanIdSearchParam = searchParams.get('paymentPlanId');
    const isSearchParams = !searchParams.size;

    const hasPaymentPlanIdSearchParam =
        typeof paymentPlanIdSearchParam === 'string' && paymentPlanIdSearchParam.trim().length > 0;

    if (hasPaymentPlanIdSearchParam) {
        return paymentPlanIdSearchParam;
    }

    if (paymentPlanId && paymentPlanId.trim().length > 0 && isSearchParams) {
        return paymentPlanId;
    }

    return null;
};

const setBookingStorage = (data: BookingState) => {
    const storage = getStorage();
    const newBooking = { ...storage.booking, ...data };
    setStorage({ booking: newBooking });
};

const getBookingStorage = (): BookingState | null => {
    const storage = getStorage();
    return storage.booking;
};

const setPaymentPlanSelectionStorage = (data: PaymentPlanSelection) => {
    const storage = getStorage();
    const newPaymentPlan = {
        ...storage.paymentPlanSelection,
        ...data,
    };
    setStorage({ paymentPlanSelection: newPaymentPlan });
};

const getPaymentPlanSelectionStorage = (): PaymentPlanSelection | null => {
    const storage = getStorage();
    const { paymentPlanSelection } = storage;

    return paymentPlanSelection;
};

const setDualOccupantData = (data: DualOccupant) => {
    setStorage({ ...getStorage(), dualOccupant: data });
};

const getDualOccupantData = (): DualOccupant | null => {
    const storage = getStorage();
    return new DualOccupant(storage.dualOccupant);
};

const setLanguageStorage = (language: LanguagesCode) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        language,
    });
};

const setVitaRefStorage = (vitaRef: string) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        amend: {
            vitaRef,
            roommates: storage.amend.roommates,
        },
    });
};

const setAmendRoommatesStorage = (roommates: RoommateData[]) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        amend: {
            vitaRef: storage.amend.vitaRef,
            roommates,
        },
    });
};

export {
    removeStorage,
    getAcademicYear,
    getBasketStorage,
    getRoomCode,
    getPromotionId,
    getCbl,
    getRebooker,
    getPaymentPlanId,
    getStorage,
    getUserStorage,
    setBasketStorage,
    setNavigationState,
    getIdToken,
    resetAuth0Storage,
    setTokenStorageExpired,
    getTokenStorageExpired,
    setRedirectState,
    clearStorageOnRoomOrYearChange,
    updateRedirectState,
    setStorage,
    setUserStorage,
    setBookingStorage,
    getBookingStorage,
    setPaymentPlanSelectionStorage,
    getPaymentPlanSelectionStorage,
    setDualOccupantData,
    getDualOccupantData,
    setLanguageStorage,
    setVitaRefStorage,
    setAmendRoommatesStorage,
};
